import type { FC, PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import type { SceneStat } from '@/constants/scenes'

import { useEffect, useRef } from 'react'
import {
    ArrowLeft,
    ArrowRight,
    Pause,
    Play
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useHomeState } from '@/hooks/use-home-state'
import { frameBus, homeCommands } from '@/lib/cloud/store'
import { introDelay } from '@/lib/intro'
import { CountUp } from '@/components/generals/count-up'

import { HOME_SCENES } from '@/constants/scenes'

interface SceneBarProps {
    hidden?: boolean
}

interface StatCellProps {
    stat: SceneStat
    epoch: number
    order: number
    className?: string
}

/** The bar draws in cell by cell during the intro, 70 ms apart. */
const CELL_STAGGER = 70

/**
 * The value sets the cell width; the label wraps inside it, so a value is never cut.
 */
const StatCell: FC<StatCellProps> = ({ stat, epoch, order, className }): ReactNode => {
    return (
        <div className={cn('intro-bar-cell flex flex-none flex-col justify-center gap-1 border-r border-hairline', className)} style={introDelay(order * CELL_STAGGER)}>
            <dt className='hud w-0 min-w-full text-mute xl:w-auto'>
                {stat.label}
            </dt>

            <dd className='hud whitespace-nowrap text-[13px] leading-5 tracking-[0.06em] text-ink xl:text-[14px]'>
                <CountUp value={stat.value} decimals={stat.decimals} suffix={stat.suffix} epoch={epoch} />
            </dd>
        </div>
    )
}

const startHover = (event: ReactPointerEvent): void => {
    if (event.pointerType === 'mouse') homeCommands.setHover(true)
}

const endHover = (): void => {
    homeCommands.setHover(false)
}

export const SceneBar: FC<SceneBarProps> = ({ hidden = false }): ReactNode => {
    const state = useHomeState()
    const stats = HOME_SCENES[state.statsScene].stats
    const progressRefs = useRef<Array<HTMLSpanElement | null>>([])

    useEffect(() => {
        return frameBus.subscribe(info => {
            const write = (elements: Array<HTMLSpanElement | null>): void => {
                elements.forEach((element, index) => {
                    if (!element) return

                    const value = index === info.scene && !info.transitioning ? info.progress : 0
                    const next = `scaleX(${value.toFixed(4)})`

                    if (element.style.transform !== next) element.style.transform = next
                })
            }

            write(progressRefs.current)
        })
    }, [])

    const PauseIcon = state.paused ? Play : Pause
    const pauseLabel = state.paused ? 'Play scene' : 'Pause scene'

    return (
        <>
            <div className='desk:hidden'>
                <dl className='no-scrollbar flex snap-x snap-mandatory overflow-x-auto border-y border-hairline'>
                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            className='intro-bar-cell flex min-w-[46vw] shrink-0 snap-start flex-col justify-center gap-1 border-r border-hairline px-4 py-4 first:pl-gutter last:border-r-0 last:pr-gutter sm:min-w-[32vw]'
                            style={introDelay(index * CELL_STAGGER)}
                        >
                            <dt className='hud text-mute'>
                                {stat.label}
                            </dt>

                            <dd className='hud whitespace-nowrap text-[14px] leading-5 tracking-[0.06em] text-ink'>
                                <CountUp value={stat.value} decimals={stat.decimals} suffix={stat.suffix} epoch={state.statsEpoch} />
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>


            <div
                onPointerEnter={startHover}
                onPointerLeave={endHover}
                aria-hidden={hidden || undefined}
                className={cn(
                    'intro-bar fixed inset-x-0 bottom-0 z-30 hidden h-[72px] border-t border-hairline bg-background/90 transition-transform duration-700 ease-instrument desk:block',
                    hidden && 'pointer-events-none translate-y-full'
                )}
            >
                <div className='mx-gutter flex h-full'>
                    <dl className='flex flex-none'>
                        {stats.map((stat, index) => (
                            <StatCell
                                key={index}
                                stat={stat}
                                epoch={state.statsEpoch}
                                order={index}
                                className={cn('px-3 xl:min-w-[168px] xl:px-5', index === 0 && 'pl-0 xl:pl-0')}
                            />
                        ))}
                    </dl>

                    <nav aria-label='Scenes' className='grid min-w-0 flex-1 grid-cols-4'>
                        {HOME_SCENES.map((scene, index) => {
                            const active = index === state.scene

                            return (
                                <button
                                    key={scene.number}
                                    type='button'
                                    tabIndex={hidden ? -1 : 0}
                                    onClick={() => homeCommands.goTo(index)}
                                    aria-current={active ? 'true' : undefined}
                                    aria-label={`Scene ${scene.number}, ${scene.tab}`}
                                    style={introDelay((stats.length + index) * CELL_STAGGER)}
                                    className={cn(
                                        'intro-bar-cell hud group relative flex min-w-0 flex-col items-start justify-center gap-0.5 border-r border-hairline px-3 text-left transition-colors duration-300 xl:flex-row xl:items-center xl:gap-3 xl:px-5',
                                        active ? 'text-ink' : 'text-mute hover:text-ink'
                                    )}
                                >
                                    <span
                                        ref={element => {
                                            progressRefs.current[index] = element
                                        }}
                                        aria-hidden='true'
                                        className='absolute inset-x-0 -top-px h-px origin-left bg-signal'
                                        style={{ transform: 'scaleX(0)' }}
                                    />

                                    <span className={cn('transition-colors duration-300', active ? 'text-signal' : 'text-mute group-hover:text-ink')} aria-hidden='true'>
                                        {scene.number}
                                    </span>

                                    <span className='max-w-full truncate tracking-[0.08em] xl:tracking-[0.14em]' aria-hidden='true'>
                                        {scene.tab}
                                    </span>
                                </button>
                            )
                        })}
                    </nav>

                    <div className='intro-bar-cell grid w-[168px] flex-none grid-cols-3' style={introDelay((stats.length + HOME_SCENES.length) * CELL_STAGGER)}>
                        <button
                            type='button'
                            tabIndex={hidden ? -1 : 0}
                            onClick={() => homeCommands.togglePause()}
                            aria-pressed={state.paused}
                            aria-label={pauseLabel}
                            className='flex items-center justify-center border-r border-hairline text-ink transition-colors duration-300 hover:bg-panel'
                        >
                            <PauseIcon className='size-4' strokeWidth={1.5} aria-hidden='true' />
                        </button>

                        <button
                            type='button'
                            tabIndex={hidden ? -1 : 0}
                            onClick={() => homeCommands.prev()}
                            aria-label='Previous scene'
                            className='flex items-center justify-center border-r border-hairline text-ink transition-colors duration-300 hover:bg-panel'
                        >
                            <ArrowLeft className='size-4' strokeWidth={1.5} aria-hidden='true' />
                        </button>

                        <button
                            type='button'
                            tabIndex={hidden ? -1 : 0}
                            onClick={() => homeCommands.next()}
                            aria-label='Next scene'
                            className='flex items-center justify-center text-ink transition-colors duration-300 hover:bg-panel'
                        >
                            <ArrowRight className='size-4' strokeWidth={1.5} aria-hidden='true' />
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
}
